import { ComponentType, useEffect, useState } from 'react';

import { ChevronRightIcon, GlobeIcon } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { FieldsetCard } from '@/components/atoms/FieldsetCard';
import { LanguageFlag } from '@/components/atoms/FlagEmoji/LanguageFlag';
import { MultiSelectField, MultiSelectOption } from '@/components/atoms/MultiSelect';
import { SectionLayout } from '@/components/atoms/SectionLayout/SectionLayout';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { buttonVariants } from '@/components/ui/button';
import { PopoverComboboxTrigger } from '@/components/ui/custom/PopoverCombobox';
import { Form, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Item, ItemActions, ItemContent, ItemMedia, ItemTitle } from '@/components/ui/item';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { hasVoiceProviderSupport } from '@/constants/voiceProviderLanguageMap';
import useBrains from '@/hooks/useBrains';
import { useSidebar } from '@/hooks/useSidebar';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { cn } from '@/lib/utils';
import {
  selectCanReadSpeechStrategy,
  useSpeechStrategy,
} from '@/modules/aiAgents/hooks/useSpeechStrategy';
import { selectIsSpeechStrategyDirty } from '@/modules/aiAgents/hooks/useSpeechStrategyForm';
import { getPermissions } from '@/redux/permissions/selectors';
import { resetSidebar } from '@/redux/sidebar/actions';
import { selectOpenSidebar } from '@/redux/sidebar/selectors';
import { Brain, BrainLanguage } from '@/shared/types/brain';
import { actions } from '@/shared/types/permissions';
import { DEFAULT_LANGUAGE } from '@/shared/types/speechStrategy';
import { RootState } from '@/shared/types/state';
import { CLEAR_DIRTY_VALUES, KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';
import { formatLanguageLabel, prioritizeLanguageOptions } from '@/util/languageOptions';

import { SPEECH_KINDS } from '../SpeechStrategy/helpers';

const FORM_ID = 'languages-form';

/** The `supported_languages` value for "reply in every language". */
const ALL_LANGUAGES = '*';

interface LanguagesForm {
  supported_languages: string[];
}

interface LanguagesFieldsetProps {
  brain: Brain;
}

const FLAG_ICONS = Object.fromEntries(
  Object.values(BrainLanguage).map((code) => [
    code,
    ({ className }: { className?: string }) => (
      <span className={cn('flex items-center', className)}>
        <LanguageFlag code={code} />
      </span>
    ),
  ])
) as Record<string, ComponentType<{ className?: string }>>;

/** "All languages" and specific languages exclude each other; the latest pick wins. */
export const toSupportedLanguages = (previous: string[], next: string[]): string[] => {
  if (next.includes(ALL_LANGUAGES) && !previous.includes(ALL_LANGUAGES)) {
    return [ALL_LANGUAGES];
  }
  return next.length > 1 ? next.filter((code) => code !== ALL_LANGUAGES) : next;
};

const LanguageRow = ({
  code,
  isPrimary,
  onClick,
}: {
  code: string;
  isPrimary?: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Item variant="outline" size="sm" asChild className="hover:bg-highlight-background">
      <button type="button" onClick={onClick}>
        <ItemMedia variant="icon">
          <LanguageFlag code={code} />
        </ItemMedia>
        <ItemContent>
          <ItemTitle>
            {formatLanguageLabel(code)}
            {isPrimary && (
              <span className="text-xs leading-4 font-normal text-foreground-muted">
                · {t('ai_agents.languages.primary')}
              </span>
            )}
          </ItemTitle>
        </ItemContent>
        <ItemActions>
          <ChevronRightIcon size={16} className="text-foreground-muted" />
        </ItemActions>
      </button>
    </Item>
  );
};

interface SpeechConfigurationsProps {
  brain: Brain;
  languages: string[];
}

/** One row per language, plus the stored languages the list leaves out, so they stay editable. */
const SpeechConfigurations = ({ brain, languages }: SpeechConfigurationsProps) => {
  const { open: openSidebar, isOpen, meta } = useSidebar();
  const { strategy } = useSpeechStrategy(brain.brain_id);
  const isSpeechDirty = useSelector(selectIsSpeechStrategyDirty);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);

  const storedLanguages = [
    ...new Set(SPEECH_KINDS.flatMap((kind) => Object.keys(strategy?.[kind] ?? {}))),
  ]
    .filter(
      (code) => code !== DEFAULT_LANGUAGE && code !== brain.language && !languages.includes(code)
    )
    .sort();

  const showSpeech = (language: string) => openSidebar('speech_language', { language });
  const openSpeech = (language: string) => {
    if (isSpeechDirty && isOpen('speech_language') && meta?.language !== language) {
      setPendingLanguage(language);
      return;
    }
    showSpeech(language);
  };

  return (
    <div className="flex flex-col gap-2">
      <LanguageRow code={brain.language} isPrimary onClick={() => openSpeech(brain.language)} />
      {[...languages, ...storedLanguages].map((code) => (
        <LanguageRow key={code} code={code} onClick={() => openSpeech(code)} />
      ))}
      {pendingLanguage && (
        <UnsavedChangesModal
          onDiscard={() => {
            setPendingLanguage(null);
            showSpeech(pendingLanguage);
          }}
          onGoBack={() => setPendingLanguage(null)}
          onOpenChange={() => setPendingLanguage(null)}
        />
      )}
    </div>
  );
};

export const LanguagesFieldset = ({ brain }: LanguagesFieldsetProps) => {
  const { t } = useTranslation();
  const canReadSpeech = useSelector(selectCanReadSpeechStrategy);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const { updateBrainAsync } = useBrains(brain.brain_id);
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  const form = useForm<LanguagesForm>({
    mode: 'onTouched',
    resetOptions: KEEP_DIRTY_VALUES,
    values: { supported_languages: brain.supported_languages ?? [] },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = form;
  useTrackFormState({ isDirty, formId: FORM_ID });
  const supportedLanguages = useWatch({ control, name: 'supported_languages' });

  // Close the speech panel this card opened when the page goes away
  useEffect(
    () => () => {
      if (selectOpenSidebar(store.getState()) === 'speech_language') {
        dispatch(resetSidebar());
      }
    },
    [store, dispatch]
  );

  const onSubmit = async (data: LanguagesForm) => {
    await updateBrainAsync({
      brain_id: brain.brain_id,
      supported_languages: data.supported_languages,
    });
    form.reset(data, CLEAR_DIRTY_VALUES);
  };

  const languageOptions: MultiSelectOption[] = [
    {
      value: ALL_LANGUAGES,
      label: t('ai_agents.languages.all_languages'),
      description: t('ai_agents.languages.all_languages_description'),
      icon: GlobeIcon,
    },
    ...prioritizeLanguageOptions(
      Object.values(BrainLanguage)
        .filter((code) => code !== brain.language)
        .map((code) => ({
          value: code,
          label: formatLanguageLabel(code),
          icon: FLAG_ICONS[code],
          description: hasVoiceProviderSupport(code)
            ? undefined
            : t('ai_agents.languages.text_only'),
        }))
    ),
  ];

  const selectedLabels = supportedLanguages.map((code) =>
    code === ALL_LANGUAGES ? t('ai_agents.languages.all_languages') : formatLanguageLabel(code)
  );
  const speechLanguages = supportedLanguages.filter(
    (code) => code !== ALL_LANGUAGES && hasVoiceProviderSupport(code)
  );

  return (
    <SectionLayout title={t('ai_agents.languages.title')}>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={isSubmitting}
            disabled={!isDirty || !canWrite}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <Label htmlFor="primary-language">
                  {t('ai_agents.languages.primary_language')}
                </Label>
                <Select value={brain.language} disabled>
                  <SelectTrigger id="primary-language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={brain.language}>
                      <LanguageFlag code={brain.language} />
                      {formatLanguageLabel(brain.language)}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm leading-5 text-foreground-muted">
                  {t('ai_agents.languages.primary_language_helper')}
                </span>
              </div>

              <FormField
                control={control}
                name="supported_languages"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('ai_agents.languages.additional_languages')}</FormLabel>
                    <MultiSelectField
                      options={languageOptions}
                      value={field.value}
                      onChange={(next) => field.onChange(toSupportedLanguages(field.value, next))}
                      placeholder={t('ai_agents.languages.select_languages')}
                      sortOptions={false}
                      disabled={!canWrite}
                      trigger={
                        <PopoverComboboxTrigger
                          disabled={!canWrite}
                          aria-label={t('ai_agents.languages.additional_languages')}
                          className={cn(
                            'h-10',
                            selectedLabels.length === 0 && 'text-foreground-muted'
                          )}
                        >
                          {selectedLabels.join(', ') || t('ai_agents.languages.select_languages')}
                        </PopoverComboboxTrigger>
                      }
                    />
                    <FormDescription>
                      {t('ai_agents.languages.additional_languages_helper')}
                    </FormDescription>
                  </FormItem>
                )}
              />

              {canReadSpeech && (
                <Accordion type="single" collapsible>
                  <AccordionItem value="speech" className="border-none">
                    <AccordionTrigger
                      className={cn(
                        buttonVariants({ variant: 'ghost-neutral', size: 'sm' }),
                        '-ml-2 flex-none items-center gap-1 hover:no-underline [&>svg]:translate-y-0'
                      )}
                    >
                      {t('ai_agents.languages.speech_configurations')}
                    </AccordionTrigger>
                    <AccordionContent className="pt-2 pb-0">
                      <SpeechConfigurations brain={brain} languages={speechLanguages} />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}
            </div>
          </FieldsetCard>
        </form>
      </Form>
    </SectionLayout>
  );
};
